import { TEST_HUB_URL } from '@/constants/constants';
import { ClosingReportData } from '@/types/reports.type';
import { apiFetch } from '@/utils/apiWrapper';

type Dates = {
  startDate: string;
  endDate: string;
};

type ReportsProps = {
  jwtToken: string;
  companyId: string;
  dates: Dates;
  stations: string[];
};

const reportUrl = `${TEST_HUB_URL}report/`;
export async function fetchOrdersByParams(
  jwtToken: string,
  companyId: string,
  dates: Dates,
  stations: string[] = [],
): Promise<ClosingReportData | undefined> {
  let query = `startDate=${dates.startDate}&endDate=${dates.endDate}`;
  if (stations.length > 0) {
    const stationsQuery = stations.join(',');
    query = `${query}&stations=${stationsQuery}`;
  }
  try {
    const res = await fetch(`${reportUrl}get-reports/${companyId}?${query}`, {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
      },
    });
    if (res.ok) {
      const data = await res.json();
      return data;
    } else {
      throw new Error('Errors.reports_finances_error');
    }
  } catch (err) {
    throw new Error('Errors.reports_finances_error');
  }
}

export const getOneTimeJWTToken = async () => {
  try {
    const response = await apiFetch(`${reportUrl}get-one-time-jwt-token`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response?.ok) {
      const data = await response.text();
      return data;
    } else {
      throw new Error('Error getting one time JWT token');
    }
  } catch (error) {
    console.error('Error getting one time JWT token:', error);
  }
};

export async function fetchAndDownloadPDF(queryUrl: string, jwtToken: string): Promise<void> {
  try {
    const url = `${reportUrl}${queryUrl}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to generate PDF');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'report.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (error) {
    console.error('Error generating PDF:', error);
  }
}

export async function fetchAndDownloadExcel(queryUrl: string, jwtToken: string): Promise<void> {
  const url = `${reportUrl}${queryUrl}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error('Failed to generate Excel');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = 'report.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function fetchAndDownloadCSV(queryUrl: string, jwtToken: string): Promise<void> {
  const url = `${reportUrl}${queryUrl}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error('Failed to generate CSV');
  }
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = 'reports.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export type FetchTransactionProps = {
  companyId: string;
  dates: { startDate: string; endDate: string };
  offset: number;
  jwtToken: string;
  stations: string[];
};
export async function getTransactions({
  companyId,
  dates,
  offset,
  jwtToken,
  stations,
}: FetchTransactionProps) {
  let url = `${reportUrl}get-orders-breakdown/${companyId}`;
  let query = `?offset=${offset}&startDate=${dates.startDate}&endDate=${dates.endDate}`;
  if (stations.length > 0) {
    const stationsQuery = stations.join(',');
    query = `${query}&stations=${stationsQuery}`;
  }
  url = `${url}${query}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      throw new Error('Error fetching transactions');
    }
  } catch (error) {
    console.error('Error fetching transactions:', error);
  }
}

export async function fetchAndDownloadBrakeDownExcel(queryUrl: string, jwtToken: string) {
  const url = `${reportUrl}generate-breakdown-excel/${queryUrl}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error('Failed to generate Excel');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = 'breakdown.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function fetchAndDownloadBrakeDownCSV(queryUrl: string, jwtToken: string) {
  const url = `${reportUrl}generate-breakdown-csv/${queryUrl}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error('Failed to generate CSV');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = 'breakdown.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export const getEOSReports = async ({ companyId, jwtToken, dates, stations }: ReportsProps) => {
  let url = `${reportUrl}eos-reports/${companyId}`;
  let query = `?startDate=${dates.startDate}&endDate=${dates.endDate}`;
  if (stations.length > 0) {
    const stationsQuery = stations.join(',');
    query = `${query}&stations=${stationsQuery}`;
  }
  url = `${url}${query}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();

    if (data.length > 0) {
      data.forEach((report: any) => {
        if (report?.closingReportData) {
          report.closingReportData = JSON.parse(report.closingReportData);
        }
      });
    }

    return data;
  } catch (err) {
    throw new Error('Something wrong fetching EOS reports');
  }
};

export async function fetchAndDownloadEosPdf(queryUrl: string, jwtToken: string): Promise<void> {
  try {
    const url = `${reportUrl}${queryUrl}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to generate PDF');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'eos-report.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (error) {
    console.error('Error generating PDF:', error);
  }
}

export const getEOSReportBySessionId = async (sessionId: string, jwtToken: string) => {
  try {
    const response = await fetch(`${reportUrl}eos-report/${sessionId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (typeof data.closingReportData === 'string') {
        data.closingReportData = JSON.parse(data.closingReportData);
      }

      return data;
    } else {
      throw new Error('Failed to fetch EOS report');
    }
  } catch (err) {
    console.error('Error in getEOSReportBySessionId:', err);
    throw err;
  }
};
